import React, { useEffect, useState } from "react";
import PageHeader from "../../../app/layout/PageHeader";
import { api } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { OrganisationDetails } from "../../types/types";
import OrganisationDetailsStep from "./steps/OrganisationDetailsStep";
import { notify } from "../../components-ui/toast";
import "./tenant-administration.css";

type InvoiceSettings = {
  invoiceEmailAddress: string;
  invoiceContactName?: string;
  defaultPaymentTermsDays: number;
};

const defaultInvoiceSettings: InvoiceSettings = {
  invoiceEmailAddress: "",
  invoiceContactName: "",
  defaultPaymentTermsDays: 30,
};

function OrganisationInvoiceDetails() {
  const [settings, setSettings] = useState<InvoiceSettings>(
    defaultInvoiceSettings,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api<InvoiceSettings | null>("/api/supplier-invoices/settings")
      .then((savedSettings) => {
        if (!cancelled) setSettings(savedSettings ?? defaultInvoiceSettings);
      })
      .catch((error) => {
        console.error("Failed to load invoice details", error);
        if (!cancelled) notify.error("Failed to load invoice details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    const invoiceEmailAddress = settings.invoiceEmailAddress.trim();
    if (!invoiceEmailAddress) {
      notify.error("Enter the organisation invoice email address");
      return;
    }

    setSaving(true);
    try {
      const saved = await api<InvoiceSettings>(
        "/api/supplier-invoices/settings",
        {
          method: "PUT",
          body: {
            ...settings,
            invoiceEmailAddress,
            invoiceContactName: settings.invoiceContactName?.trim() || null,
          },
        },
      );
      setSettings(saved);
      notify.success("Invoice details saved");
    } catch (error) {
      console.error("Failed to save invoice details", error);
      notify.error("Failed to save invoice details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card organisation-invoice-details">
      <div className="card-header">
        <div>
          <div className="card-title">Invoice details</div>
          <p className="organisation-invoice-details__hint">
            Submitted supplier invoices will be sent to this contact.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading invoice details...</p>
      ) : (
        <>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="organisation-invoice-email">
                Invoice email
              </label>
              <input
                className="form-input"
                id="organisation-invoice-email"
                type="email"
                value={settings.invoiceEmailAddress}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    invoiceEmailAddress: event.target.value,
                  }))
                }
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="organisation-invoice-contact">
                Invoice contact name
              </label>
              <input
                className="form-input"
                id="organisation-invoice-contact"
                value={settings.invoiceContactName ?? ""}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    invoiceContactName: event.target.value,
                  }))
                }
                autoComplete="name"
              />
            </div>

            <div className="form-group organisation-invoice-details__terms">
              <label className="form-label" htmlFor="organisation-payment-terms">
                Payment terms (days)
              </label>
              <input
                className="form-input"
                id="organisation-payment-terms"
                type="number"
                min="0"
                max="365"
                value={settings.defaultPaymentTermsDays}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    defaultPaymentTermsDays: Number(event.target.value),
                  }))
                }
              />
            </div>
          </div>

          <div className="organisation-invoice-details__actions">
            <button
              type="button"
              className="form-submit-button"
              disabled={saving}
              onClick={() => void save()}
            >
              {saving ? "Saving..." : "Save invoice details"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export default function OrganisationDetailsPage() {
  const { selectedTenant } = useAuth();
  const [organisation, setOrganisation] = useState<OrganisationDetails | null>(
    null,
  );
  const [loading, setLoading] = useState(Boolean(selectedTenant));

  useEffect(() => {
    if (!selectedTenant) {
      setOrganisation(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setOrganisation(null);

    api<OrganisationDetails>(
      `/api/tenant-administration/${selectedTenant.tenantId}/organisation`,
    )
      .then((details) => {
        if (!cancelled) setOrganisation(details);
      })
      .catch((error) => {
        console.error("Failed to load organisation details", error);
        if (!cancelled) notify.error("Failed to load organisation details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTenant]);

  return (
    <div className="tenant-admin-page">
      <PageHeader
        title="Organisation Details"
        subtitle="Manage the details for the active organisation."
      />

      {loading ? (
        <p className="muted">Loading organisation details...</p>
      ) : selectedTenant && organisation ? (
        <>
          <OrganisationDetailsStep
            tenantId={selectedTenant.tenantId}
            partyId={organisation.partyId}
            onSaved={() => undefined}
          />
          <OrganisationInvoiceDetails key={selectedTenant.tenantId} />
        </>
      ) : (
        <div className="card tenant-admin-empty">
          Select an active organisation from your profile to manage its details.
        </div>
      )}
    </div>
  );
}
